import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserContext, ACR_BASIC } from '@ilteducation/ilt-auth';
import {
  Result,
  Context,
  TargetAssociation,
  CallerAssociation,
  Reason,
  GetGroupMembershipsFunction,
} from '@ilteducation/customer-mgmt-permissions';
import { SlimOrganization, useMyGroups, useMyOrganizations } from '@ilteducation/my-account';
import { CustomerMgmtTypes, useGetUserGroups } from '@ilteducation/customer-mgmt';

// Cleared by revalidatePermissionCaches() (mount) and clearTargetMembershipsCache() (on membership:updated).
const targetMembershipsCache = new Map<string, Promise<CustomerMgmtTypes.MembershipOutput[]>>();

export function usePermissions() {
  const userContext = useUserContext();
  const { user, subscriptions } = storeToRefs(userContext);
  const { organizations, loadMyOrganizations } = useMyOrganizations(false);
  const { groups, loadMyGroups } = useMyGroups(false);

  const permissionContext = computed(() => ({
    sub: user.value.uid,
    acr: userContext.acr ?? ACR_BASIC,
    scopes: userContext.scopes,
    realm: userContext.realm,
    subscriptions: subscriptions.value,
  }));

  /**
   * The role here is deliberately not derived. It comes from my-account, which passes userville's
   * stored value through untouched, so an administrator already reads as `admin`. Target
   * associations take the other route, through customer-mgmt's `asEmployee` projection, which
   * rewrites `admin` to `employee` plus `isAdministrator` and therefore has to be converted back.
   *
   * The two agree only because their sources differ. Should my-account ever adopt the same
   * projection, administrators would quietly read as employees, fail `isAdminRole`, and lose the
   * right to create staff, with nothing failing to compile.
   *
   * TODO: settle on one representation of an administrator across the API surface, so neither the
   * permission checks nor the interface has to know which side of the projection a role came from.
   */
  const createCallerAssociation = (userUid: string, organization: SlimOrganization) => ({
    role: organization.role,
    organization,
    user: { uid: userUid },
  });

  const getCallerAssociation = async (
    callerUid: string,
    organizationUid: string,
  ): Promise<CallerAssociation | undefined> => {
    const org = organizations.value.data.find((org) => org.uid === organizationUid);
    if (org) {
      return createCallerAssociation(callerUid, org);
    }
    return undefined;
  };

  const getCallerAssociations = async (callerUid: string, organizationUids: string[]): Promise<CallerAssociation[]> =>
    organizations.value.data
      .filter((org) => organizationUids.includes(org.uid))
      .map((org) => createCallerAssociation(callerUid, org));

  const fetchUserGroupMemberships = async (
    userUid: string,
    organizationUids: string[],
  ): Promise<CustomerMgmtTypes.MembershipOutput[]> => {
    if (organizationUids.length === 0) {
      return [];
    }
    const key = `${userUid}:${[...organizationUids].sort().join(',')}`;
    if (!targetMembershipsCache.has(key)) {
      const { loadUserGroups } = useGetUserGroups();
      targetMembershipsCache.set(
        key,
        loadUserGroups(userUid, organizationUids).then((res) => res?.data ?? []),
      );
    }
    return targetMembershipsCache.get(key) as Promise<CustomerMgmtTypes.MembershipOutput[]>;
  };

  /**
   * Resolves a user's group memberships for the customer-management permission checks.
   *
   * For the *caller* it is backed by the `my-account` service —
   * `/me/groups` only exposes the caller's own groups, and `loadMyGroups` caches them.
   * For any *other* user (e.g. the edit target) it resolves their memberships from
   * customer-mgmt, so group-level-restriction checks can compare a common group between
   * caller and target.
   */
  const getGroupMemberships: GetGroupMembershipsFunction = async (userUid, organizationUids, groupUids) => {
    const groupUidsSet = groupUids ? new Set(groupUids) : null;

    if (userUid === user.value.uid) {
      await loadMyGroups();

      const orgUidsSet = organizationUids ? new Set(organizationUids) : null;
      return groups.value.data
        .filter((g) => !orgUidsSet || orgUidsSet.has(g.parent.uid))
        .filter((g) => !groupUidsSet || groupUidsSet.has(g.uid))
        .map((g) => ({
          user: { uid: userUid },
          group: { uid: g.uid, organization: { uid: g.parent.uid } },
        }));
    }

    const memberships = await fetchUserGroupMemberships(userUid, organizationUids ?? []);
    return memberships
      .filter((m) => !groupUidsSet || groupUidsSet.has(m.group.uid))
      .map((m) => ({
        user: { uid: userUid },
        group: { uid: m.group.uid, organization: { uid: m.group.schoolUnit.uid } },
      }));
  };

  const isSearchUserOutput = (
    user: CustomerMgmtTypes.GetUserResponse | CustomerMgmtTypes.SearchUserOutput,
  ): user is CustomerMgmtTypes.SearchUserOutput => {
    return typeof user.identifiers === 'object' && !Array.isArray(user.identifiers);
  };

  const isSearchAssociationOutput = (
    association: CustomerMgmtTypes.UserAssociationOutput | CustomerMgmtTypes.SearchAssociationOutput,
  ): boolean => {
    return 'organization' in association;
  };

  /**
   * customer-mgmt's `asEmployee` projection sends an administrator to the client as an employee
   * carrying `isAdministrator`, so anything reasoning about the role has to convert it back.
   * Search results are already projected differently and carry the role as-is.
   */
  const getRoleFromAssociation = (
    association: CustomerMgmtTypes.UserAssociationOutput | CustomerMgmtTypes.SearchAssociationOutput,
  ) => {
    if (isSearchAssociationOutput(association)) {
      return (association as CustomerMgmtTypes.SearchAssociationOutput).role;
    }

    return (association as CustomerMgmtTypes.UserAssociationOutput).isAdministrator
      ? 'admin'
      : (association as CustomerMgmtTypes.UserAssociationOutput).role;
  };

  const createIdentifiersFromUser = (user: CustomerMgmtTypes.UserOutput | CustomerMgmtTypes.SearchUserOutput) => {
    if (isSearchUserOutput(user)) {
      return user.identifiers;
    }

    return user.identifiers.reduce<TargetAssociation['user']['identifiers']>((acc, identifier) => {
      acc[identifier.type] = { source: identifier.source };
      return acc;
    }, {});
  };

  const createTargetAssociation = (
    user: CustomerMgmtTypes.GetUserResponse | CustomerMgmtTypes.SearchUserOutput,
    association: CustomerMgmtTypes.UserAssociationOutput | CustomerMgmtTypes.SearchAssociationOutput,
  ): TargetAssociation => ({
    role: getRoleFromAssociation(association),
    organization: {
      uid: isSearchAssociationOutput(association)
        ? (association as CustomerMgmtTypes.SearchAssociationOutput).organization.uid
        : (association as CustomerMgmtTypes.UserAssociationOutput).schoolUnit.uid,
    },
    user: {
      uid: user.uid,
      active: user.active,
      source: user.source,
      identifiers: createIdentifiersFromUser(user),
    },
    source: association.source,
  });

  const createGetTargetAssociation =
    (user: CustomerMgmtTypes.GetUserResponse) =>
    async (_userUid: string, organizationUid: string): Promise<TargetAssociation | undefined> => {
      const assoc = user.associations.find((org) => org.schoolUnit.uid === organizationUid);
      if (assoc) {
        return createTargetAssociation(user, assoc);
      }
      return undefined;
    };

  const createGetTargetAssociations =
    (user: CustomerMgmtTypes.GetUserResponse | CustomerMgmtTypes.SearchUserOutput) =>
    async (_userUid: string): Promise<TargetAssociation[]> => {
      return user.associations.map((assoc) => createTargetAssociation(user, assoc));
    };

  type ReporterWithReasonType = { allowed: false; reason: Reason } | { allowed: true; reason: undefined };
  const reporterWithReason = (result: Result, _context: Context): ReporterWithReasonType => {
    if (result === null) {
      return { allowed: true, reason: undefined };
    }
    return { allowed: false, reason: result };
  };

  const reporterBoolean = (result: Result, _context: Context) => result === null;

  const revalidatePermissionCaches = (): void => {
    loadMyGroups({ force: true });
    loadMyOrganizations({ force: true });
    targetMembershipsCache.clear();
  };

  // Lazy invalidation: drop cached target memberships; they refetch on the next gating pass.
  const clearTargetMembershipsCache = (): void => {
    targetMembershipsCache.clear();
  };

  return {
    permissionContext,
    getRoleFromAssociation,
    revalidatePermissionCaches,
    clearTargetMembershipsCache,
    createTargetAssociation,
    getCallerAssociations,
    getCallerAssociation,
    getGroupMemberships,
    createGetTargetAssociation,
    createGetTargetAssociations,
    reporterWithReason,
    reporterBoolean,
  };
}
